import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES_PATH } from "../../constants";
import { useCreditProposalResult } from "../../contexts/CreditProposalResultContext";
import OcrService from "../../services/Ocr";
import CreditProposalService from "../../services/CreditProposal";
import hubInstance from "../../services/Websocket";
import { useWebsocket } from "../../contexts/WebsocketContext";
import AnalyticsService from "../../services/Analytics";
import AnalyticsHelper from "../../utils/analyticsHelper";
import dayjs from "dayjs";
import { useMerchant } from "../../contexts/MerchantContext";
import PostMessageService from "../../services/PostMessage";
import { DecisionStatus } from "../../interfaces/DecisionEngine";
import { DenialStep } from "../../interfaces/Analytics";
import TokenService from "../../services/Token";
import { Ocr, OcrStepProps } from "@pepper/ocr-react";
import InsightService from "../../services/Insight";
import { EnvType } from "../../interfaces/EnvType";
import { OcrError, OcrErrorImage, OcrErrorImageContainer, OcrErrorText, OcrSlot } from "./OcrStyledComponent";
import OcrErrorImageSrc from "../../assets/images/ocr/ocr-error.svg";
import ViewModeService from "../../services/ViewMode";
import CustomerDeviceService from "../../services/CustomerDevice";

interface OcrProps {
  loadingEvent: (data: boolean) => void;
  closingEvent: (data: boolean) => void;
  helpOverlayEvent: (data: boolean) => void;
}

export const Documentation: React.FC<OcrProps> = ({
  loadingEvent,
  closingEvent,
  helpOverlayEvent,
}) => {
  const navigate = useNavigate();
  const { creditProposalResult } = useCreditProposalResult();
  const { websocketTexts, createWebsocketTexts, deleteWebsocketTexts } = useWebsocket();
  const { isNemuru, isPostDataMessages } = useMerchant();
  const [iframeUrl, setIframeUrl] = useState("");
  const [identityDocument, setIdentityDocument] = useState("");
  const [hasScanError, setHasScanError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const errorResetTimeoutRef = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);

  const [isApp, setisApp] = useState<boolean>(false);
  const [device, setDevice] = useState<string | null>(null);

  const websocketList: string[] = [];

  const startTime = dayjs();

  const getDocumentation = async () => {
    //loadingEvent(true);
    if (isNemuru) {
      // Quitamos la x de cierre para Nemuru
      closingEvent(false);
    }
    try {
      const resp = await OcrService.identifyDocument(
        creditProposalResult!.credit_proposal_id,
      );
      if (resp) {
        setIframeUrl(resp.url_access);
        setIdentityDocument(resp.identity_document_id);
      }
    } catch (error) {
      navigate(ROUTES_PATH.UNKNOWN_ERROR);
    } finally {
      loadingEvent(false);
    }
  };

  const getPADecision = async (creditProposalId: string, counter: number) => {
    loadingEvent(true);
    counter++;
    try {
      let decisionStatus = { signatureReady: false, isDecisionPending: true };
      const decisionResp =
        await CreditProposalService.getDecision(creditProposalId);

      if (decisionResp) {
        loadingEvent(false);
        switch (decisionResp.decision) {
          case DecisionStatus.APPROVED:
          case DecisionStatus.NOT_NECESSARY:
            decisionStatus = { signatureReady: true, isDecisionPending: false };
            navigate(ROUTES_PATH.SIGNATURE, { state: { decisionStatus } });
            break;
          case DecisionStatus.NO_DECISION:
          case DecisionStatus.PENDING:
            decisionStatus = { signatureReady: true, isDecisionPending: true };
            navigate(ROUTES_PATH.SIGNATURE, { state: { decisionStatus } });
            break;
          case DecisionStatus.DENIED:
            AnalyticsService.sendLoanRefused({ denial_step: DenialStep.PAC });
            navigate(ROUTES_PATH.DENIAL);
            break;
          case DecisionStatus.MISSING_DATA:
            navigate(ROUTES_PATH.UNKNOWN_ERROR);
            break;
          default:
            navigate(ROUTES_PATH.UNKNOWN_ERROR);
            break;
        }
      }
    } catch (error) {
      if (`${error}`.includes("409") && counter <= 11) {
        setTimeout(() => {
          getPADecision(creditProposalId, counter);
        }, 3000);
      } else {
        loadingEvent(false);
        navigate(ROUTES_PATH.UNKNOWN_ERROR);
      }
    }
  };

  const handleSuccess = () => {
    if (creditProposalResult?.product === "PLS") {
      createWebsocketTexts(websocketList);
      getPADecision(creditProposalResult.credit_proposal_id, 0);
      // await CreditProposalService.createBankreader(
      //   creditProposalResult.credit_proposal_id,
      //   NotificationType.WEB
      // );
      // navigate(ROUTES_PATH.BANKREADER);
    } else {
      navigate(ROUTES_PATH.PAYMENT);
    }
    if (isPostDataMessages) {
      // Nemuru post message Ocr OK
      PostMessageService.OcrSuccessful(creditProposalResult.credit_proposal_id);
    }
  };

// Oculta el banner de error y cancela el cierre automático al elegir un método de subida
  const clearScanError = () => {
    if (errorResetTimeoutRef.current) {
      clearTimeout(errorResetTimeoutRef.current);
      errorResetTimeoutRef.current = undefined;
    }

    setHasScanError(false);
    setErrorMessage("");
  };

  const handleError = (message: string) => {
    if (errorResetTimeoutRef.current) {
      clearTimeout(errorResetTimeoutRef.current);
    }

    setHasScanError(true);
    setErrorMessage(message);

    errorResetTimeoutRef.current = setTimeout(() => {
      setHasScanError(false);
      setErrorMessage("");
      errorResetTimeoutRef.current = undefined;
    }, 8000);
  };

  useEffect(() => {
    return () => {
      if (errorResetTimeoutRef.current) {
        clearTimeout(errorResetTimeoutRef.current);
      }
    };
  }, []);

  const handleMessage = async (event: any) => {
    if (event.data.type === "OCR_PEPPER") {
      if (event.data.message === "success") {
        if (creditProposalResult?.product === "PLS") {
          createWebsocketTexts(websocketList);
          getPADecision(creditProposalResult.credit_proposal_id, 0);
          // await CreditProposalService.createBankreader(
          //   creditProposalResult.credit_proposal_id,
          //   NotificationType.WEB
          // );
          // navigate(ROUTES_PATH.BANKREADER);
        } else {
          navigate(ROUTES_PATH.PAYMENT);
        }
        if (isPostDataMessages) {
          // Nemuru post message Ocr OK
          PostMessageService.OcrSuccessful(
            creditProposalResult.credit_proposal_id,
          );
        }
      } else if (event.data.message === "error") {
        navigate(ROUTES_PATH.UNKNOWN_ERROR);
      }
    }
  };

  const saveWebsocketText = (message: string) => {
    let messageRecieved = false;
    websocketTexts?.forEach((data: any) => {
      if (message === data) {
        messageRecieved = true;
      }
    });
    !messageRecieved && websocketList.push(message);
  };

  const handleClose = () => {
    const step_number = AnalyticsHelper.getStepLocation(location.pathname);
      AnalyticsService.sendBackExit(step_number);
      if (CustomerDeviceService.getCustomerDevice()) {
        window.location.href = 'https://pepperfinance.es/';
      } else {
        // En Quit el caso iOS se resuelve con un <a>; aquí el botón lo pinta la
        // librería de Ocr, así que enviamos el deep link también para iOS
        if (isApp && (device === 'android' || device === 'ios')) {
          window.location.href = 'peppermerchants://home';
        } else {
          deleteWebsocketTexts();
          PostMessageService.closeCEX();
        }
      }
  }

  useEffect(() => {
    setisApp(ViewModeService.getViewMode() === 'APP' ? true : false);
    setDevice(ViewModeService.getDevice());
  }, [isApp, device]);

  useEffect(() => {
    loadingEvent(false);
    getDocumentation();
    const messageListener = (event: MessageEvent) => {
      handleMessage(event);
    };
    window.addEventListener("message", messageListener);
    return () => window.removeEventListener("message", messageListener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (websocketTexts && websocketTexts.length > 0) {
      websocketTexts.forEach((data: any) => {
        websocketList.push(data);
      });
    }
    hubInstance.ocrStartListener().subscribe(() => {
      const msg = "El cliente está adjuntando la documentación";
      saveWebsocketText(msg);
    });
    hubInstance.ocrEndListener().subscribe(() => {
      const msg = "Documentación adjuntada con éxito";
      saveWebsocketText(msg);
    });
    AnalyticsService.continueOcr({
      time_elapsed: AnalyticsHelper.getTimeElapse({
        initTime: startTime,
        finishTime: dayjs(),
      }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {identityDocument && iframeUrl && (
        <OcrSlot>
          {hasScanError && (
            <OcrError>
              <OcrErrorImageContainer>
                <OcrErrorImage src={OcrErrorImageSrc} alt="Ocr Error Image" />
              </OcrErrorImageContainer>
              <OcrErrorText>
                {errorMessage ||
                  "¡Vaya! No hemos leer el fichero adjunto. Inténtalo de nuevo con otro."}
              </OcrErrorText>
            </OcrError>
          )}
          <Ocr
            accessToken={TokenService.getToken() || ""}
            environment={
              (import.meta.env.VITE_ENV as EnvType) || ("staging" as EnvType)
            }
            uuid={identityDocument}
            handleSuccess={handleSuccess}
            handleError={handleError}
            // TODO: implementar gestión handleClose
            handleClose={handleClose}
            onOptionSelected={clearScanError}
            greenCardHelpOverlay={(state: boolean) => {
              helpOverlayEvent(state);
            }}
            stepEvent={(step: string, props?: OcrStepProps) => {
              // El tipo de documento marca la sesión en Clarity (tag, no evento)
              if (step === "DOCUMENT_TYPE" && props?.document_type) {
                AnalyticsService.setDocumentTypeTag(props.document_type);
              }
            }}
            scanDocument={undefined}
            connectionString={import.meta.env.VITE_CONNECTION_STRING}
            insightsEventData={(e: any) => {
              if (
                e.eventName === "ResponseError" ||
                e.eventName === "RequestError"
              ) {
                InsightService.logException(e.properties.error);
              } else {
                InsightService.logEvent(e.eventName, e.properties);
              }
            }}
            urlAccessQr={iframeUrl}
            origin={"CLIENT"}
          />
        </OcrSlot>
      )}
    </>
  );
};
